import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@zond/ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { getAppFullName, getLogo } from '../../../utils/get-logo';
import { useResetPassword } from './auth.queries';

export default function ResetPasswordPage() {
  const { t } = useTranslation('auth');
  const { mutate: resetPassword, isPending, isSuccess, isError } = useResetPassword();

  const resetPasswordSchema = z
    .object({
      password: z.string().min(8, t('validation.passwordMin')),
      confirmPassword: z.string().min(8, t('validation.passwordMin')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.passwordsDontMatch'),
      path: ['confirmPassword'],
    });

  const form = useForm({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const onSubmit = (data: z.infer<typeof resetPasswordSchema>) => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      return;
    }

    resetPassword({ token, password: data.password });
  };

  if (isSuccess) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <div className="flex items-center gap-2 self-center font-medium">
            <img src={getLogo()} alt={t('logoAlt')} className="h-6 w-6" />
            {getAppFullName()}
          </div>
          <Card className="w-full">
            <CardHeader className="text-center">
              <CardTitle className="text-xl">{t('resetPassword.title')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4 text-center">
              <p>{t('resetPassword.success')}</p>
              <div className="flex justify-center">
                <Button asChild>
                  <Link to="/login">{t('resetPassword.loginButton')}</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 bg-muted p-6 md:p-10">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <div className="flex items-center gap-2 self-center font-medium">
          <img src={getLogo()} alt={t('logoAlt')} className="h-6 w-6" />
          {getAppFullName()}
        </div>
        <Card className="w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{t('resetPassword.title')}</CardTitle>
            <CardDescription>{t('resetPassword.description')}</CardDescription>
          </CardHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardContent className="grid gap-4">
                <div className="grid gap-2">
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('resetPassword.passwordLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            {...field}
                            placeholder={t('resetPassword.passwordPlaceholder')}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('resetPassword.confirmPasswordLabel')}</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            {...field}
                            placeholder={t('resetPassword.passwordPlaceholder')}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                {isError && <p className="text-red-500 text-center">{t('resetPassword.errors.failed')}</p>}
              </CardContent>
              <CardFooter className="flex flex-col gap-2">
                <Button type="submit" className="w-full" disabled={isPending}>
                  {isPending ? t('resetPassword.submitting') : t('resetPassword.submit')}
                </Button>
                <Button variant="link" className="px-0" asChild>
                  <Link to="/login">{t('resetPassword.backToSignIn')}</Link>
                </Button>
              </CardFooter>
            </form>
          </Form>
        </Card>
      </div>
    </div>
  );
}
